import type { ReactElement } from 'react';
import { Icon } from './ui/Icon.js';
import type { CaptureMode, SearchStatus } from '../types/index.js';

/**
 * Capture-provenance + web-search legibility UI (Response Browser detail view).
 *
 * The driving principle: a "Sources (0)" response must display its REASON.
 * A zero is no longer silent — the search-status line distinguishes
 * "search not enabled" from "search invoked but cited nothing" from a
 * GEO Observatory extraction gap (amber warning).
 *
 * All fields are optional/tolerant of older rows captured before this
 * telemetry existed; each piece renders nothing (or "unknown") when absent.
 */

const CAPTURE_MODE_LABELS: Record<CaptureMode, string> = {
  api: 'API',
  consumer_gui: 'Consumer GUI',
  third_party_proxy: 'Proxy',
};

const CAPTURE_MODE_TITLES: Record<CaptureMode, string> = {
  api: 'Captured via the engine API.',
  consumer_gui: 'Captured from the consumer app interface.',
  third_party_proxy: 'Captured through a third-party proxy.',
};

/** Small, neutral badge naming how this response was captured. */
export function CaptureModeBadge({ captureMode }: { captureMode?: CaptureMode }): ReactElement | null {
  if (!captureMode) return null;
  const label = CAPTURE_MODE_LABELS[captureMode];
  if (!label) return null;

  return (
    <span
      data-testid="capture-mode-badge"
      title={CAPTURE_MODE_TITLES[captureMode]}
      className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600"
    >
      {label}
    </span>
  );
}

/**
 * Plain-language web-search status line. Renders even (especially) when there
 * are 0 sources — this is what makes a zero legible.
 */
export function SearchStatusLine({
  searchStatus,
  searchInvocations,
  sourcesReturnedByEngine,
}: {
  searchStatus?: SearchStatus;
  searchInvocations?: number;
  sourcesReturnedByEngine?: number | null;
}): ReactElement | null {
  // Older rows may carry no status at all — treat as 'unknown' rather than hiding.
  const status: SearchStatus = searchStatus ?? 'unknown';

  let text: string;
  switch (status) {
    case 'invoked': {
      const searches = searchInvocations ?? 0;
      const returned = sourcesReturnedByEngine ?? 0;
      text = `Web search: invoked (${searches} ${searches === 1 ? 'search' : 'searches'}, ${returned} ${returned === 1 ? 'source' : 'sources'})`;
      break;
    }
    case 'enabled_not_invoked':
      text = 'Web search: not invoked — weights-only answer';
      break;
    case 'not_enabled':
      text = 'Web search: not enabled for this capture';
      break;
    // Spec 199: the search ran, the engine answered nothing. Engine-neutral by
    // design (only google_aio can emit this value today), and a literal string
    // rather than anything derived from other fields.
    case 'ran_no_answer':
      text = 'Web search: ran — the engine produced no AI answer (search results only)';
      break;
    case 'unknown':
    default:
      text = 'Web search: status unknown for this capture';
      break;
  }

  return (
    <p data-testid="search-status-line" className="text-xs text-slate-500">
      {text}
    </p>
  );
}

/**
 * Amber warning shown only when the engine reported more citations than
 * GEO Observatory managed to parse — surfaces a possible extraction gap rather than a
 * silently-deflated source count. Renders nothing when the counts match.
 */
export function ExtractionDiscrepancyWarning({
  sourcesReturnedByEngine,
  sourcesExtractedByBeacon,
}: {
  sourcesReturnedByEngine?: number | null;
  sourcesExtractedByBeacon?: number | null;
}): ReactElement | null {
  // Both must be present and the engine count must exceed GEO Observatory's. A NULL
  // `sourcesReturnedByEngine` (spec 160 — body-parsed engines with no structured
  // source channel) means there's nothing to compare, so no discrepancy fires.
  if (sourcesReturnedByEngine == null || sourcesExtractedByBeacon == null) return null;
  if (sourcesReturnedByEngine <= sourcesExtractedByBeacon) return null;

  return (
    <div
      data-testid="extraction-discrepancy"
      className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
    >
      <Icon name="bx-error" className="mt-0.5 shrink-0 text-amber-600" />
      <span>
        The engine returned {sourcesReturnedByEngine}{' '}
        {sourcesReturnedByEngine === 1 ? 'citation' : 'citations'} but GEO Observatory parsed{' '}
        {sourcesExtractedByBeacon} — possible extraction gap.
      </span>
    </div>
  );
}

/**
 * Spec 160 — explains a NULL `sources_returned_by_engine` next to a non-empty
 * sources list. Engines like Gemini/Mistral/google_aio surface citations in the
 * response body rather than a structured source array, so GEO Observatory parses them
 * from the text. Without this, a "Sources (N>0)" panel would sit next to a bare
 * "0 returned by engine" and misread as an engine that returned nothing.
 * Renders only when there's no structured channel (null) AND sources were
 * actually surfaced.
 */
export function BodyParsedSourcesNote({
  sourcesReturnedByEngine,
  sourcesExtractedByBeacon,
  displayedSourceCount,
}: {
  sourcesReturnedByEngine?: number | null;
  sourcesExtractedByBeacon?: number | null;
  displayedSourceCount?: number;
}): ReactElement | null {
  // A real number (incl. 0) means the engine DOES expose a structured count —
  // nothing to explain here (the search-status line + discrepancy cover it).
  if (sourcesReturnedByEngine != null) return null;
  const surfaced = (sourcesExtractedByBeacon ?? 0) > 0 || (displayedSourceCount ?? 0) > 0;
  if (!surfaced) return null;

  return (
    <p
      data-testid="sources-body-parsed-note"
      title="This engine returns no structured source list; GEO Observatory parsed these citations from the response text."
      className="flex items-start gap-1.5 text-xs text-slate-400"
    >
      <Icon name="bx-info-circle" className="mt-0.5 shrink-0" />
      <span>
        This engine doesn&apos;t return a structured source list — the citations
        above were parsed by GEO Observatory from the response text.
      </span>
    </p>
  );
}

/**
 * Short methodology note for the Sources panel: an API capture can differ from
 * what a person sees in the consumer ChatGPT/Gemini app.
 */
export function SourcesMethodologyNote({ captureMode }: { captureMode?: CaptureMode }): ReactElement {
  return (
    <p
      data-testid="sources-methodology-note"
      title="This is an API capture; it can differ from what a person sees in the consumer ChatGPT/Gemini app."
      className="flex items-start gap-1.5 text-xs text-slate-400"
    >
      <Icon name="bx-info-circle" className="mt-0.5 shrink-0" />
      <span>
        {captureMode === 'api' || captureMode == null
          ? 'This is an API capture; it can differ from what a person sees in the consumer ChatGPT/Gemini app.'
          : 'How sources are captured can differ from what a person sees in the consumer ChatGPT/Gemini app.'}
      </span>
    </p>
  );
}
